import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { S3FileService } from '../../core/services/s3-file.service';

export interface UploadedProductImage {
  id: string;
  key: string | null;
  fileUrl: string | null;
  fileName: string;
  preview: string;
}

export interface ImageTile {
  id: string;
  fileName: string;
  key: string | null;
  preview: string | null;
  isUploading: boolean;
  error?: string;
}

@Component({
  selector: 'app-image-crop-upload',
  standalone: false,
  templateUrl: './image-crop-upload.component.html',
  styleUrl: './image-crop-upload.component.scss',
})
export class ImageCropUploadComponent implements OnDestroy{

  /** Products retain multi-image uploads; identity/logo callers opt into one. */
  @Input() maxFiles = Number.MAX_SAFE_INTEGER;
  @Input() sidebarSafe = false;

  @Output() onPendingImageCreated = new EventEmitter<ImageTile[]>();
  @Output() onSingleImageUploadFinished = new EventEmitter<ImageTile>();
  @Output() onAllImagesUploadFinished = new EventEmitter<ImageTile>();
  @Output() onImageUploadError = new EventEmitter<string>();


  showCropModal = false;
  
  imageUploadError = '';

  cropModalFileName = '';
  cropModalImageSrc: string | null = null;
  cropIsApplying = false;

  cropImageWidth = 0;
  cropImageHeight = 0;
  cropZoom = 1;
  cropMaxZoom = 1;
  cropCenterX = 0;
  cropCenterY = 0;
  cropPreviewScale = 1;

  private cropResolve: ((file: File | null) => void) | null = null;
  private cropSourceFile: File | null = null;
  private cropSourceImage: HTMLImageElement | null = null;
  private cropSourceObjectUrl: string | null = null;

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly s3FileService: S3FileService,
    private readonly cdr: ChangeDetectorRef,
  ) {

  }

  async onImagesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const selectedFiles = Array.from(input.files ?? []).filter((file) =>
      file.type.startsWith('image/')
    ).slice(0, Math.max(1, Math.floor(this.maxFiles)));

    if (!selectedFiles.length) {
      input.value = '';
      return;
    }

    const croppedFiles = await this.cropSelectedFiles(selectedFiles);
    if (!croppedFiles.length) {
      input.value = '';
      return;
    }

    this.imageUploadError = '';


    const pendingImages = croppedFiles.map((file) => this.createPendingImage(file));
    this.onPendingImageCreated.emit(pendingImages)
    this.cdr.detectChanges();

    await Promise.allSettled(
      pendingImages.map((image, index) =>
        this.uploadPendingImage(image, croppedFiles[index])
      )
    );

 
    input.value = '';
    this.cdr.detectChanges();
  }

  private createPendingImage(file: File): ImageTile {
    return {
      id: this.createImageId(),
      fileName: file.name,
      key: null,
      preview: null,
      isUploading: true,
    };
  }

  private createImageId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  closeModal() {
    this.showCropModal = false;
  }

  onCropZoomChanged(event: Event): void {
    const value = Number((event.target as HTMLInputElement)?.value);
    if (!Number.isFinite(value)) {
      return;
    }

    this.cropZoom = this.clamp(value, 1, this.cropMaxZoom);
    this.clampCropCenter();
  }

  onCropCenterXChanged(event: Event): void {
    const value = Number((event.target as HTMLInputElement)?.value);
    if (!Number.isFinite(value)) {
      return;
    }

    this.cropCenterX = this.clamp(value, this.cropCenterXMin, this.cropCenterXMax);
  }

  onCropCenterYChanged(event: Event): void {
    const value = Number((event.target as HTMLInputElement)?.value);
    if (!Number.isFinite(value)) {
      return;
    }

    this.cropCenterY = this.clamp(value, this.cropCenterYMin, this.cropCenterYMax);
  }

  async confirmCropSelection(): Promise<void> {
    if (!this.cropResolve || !this.cropSourceFile || !this.cropSourceImage || this.cropIsApplying) {
      return;
    }

    this.cropIsApplying = true;

    try {
      const croppedFile = await this.buildCroppedSquareFile(this.cropSourceFile, this.cropSourceImage);
      this.resolveCropRequest(croppedFile);
    } catch (_error) {
      this.snackBar.open('Failed to crop image. Please try again.', 'Close', {
        duration: 4500,
        panelClass: ['error-snackbar'],
        verticalPosition: 'top',
        horizontalPosition: 'center',
      });
      this.resolveCropRequest(null);
    }
  }

  cancelCropSelection(): void {
    if (this.cropIsApplying) {
      return;
    }

    this.resolveCropRequest(null);
  }

  get cropDisplayWidth(): number {
    return this.cropImageWidth * this.cropPreviewScale;
  }

  get cropDisplayHeight(): number {
    return this.cropImageHeight * this.cropPreviewScale;
  }

  get cropSizePixels(): number {
    return this.currentCropSize * this.cropPreviewScale;
  }

  get cropSelectionLeftPixels(): number {
    return (this.cropCenterX - this.currentCropSize / 2) * this.cropPreviewScale;
  }

  get cropSelectionTopPixels(): number {
    return (this.cropCenterY - this.currentCropSize / 2) * this.cropPreviewScale;
  }

  get cropCenterXMin(): number {
    return this.currentCropSize / 2;
  }

  get cropCenterXMax(): number {
    return this.cropImageWidth - this.currentCropSize / 2;
  }

  get cropCenterYMin(): number {
    return this.currentCropSize / 2;
  }

  get cropCenterYMax(): number {
    return this.cropImageHeight - this.currentCropSize / 2;
  }

  get cropOutputSize(): number {
    return Math.max(1, Math.round(this.currentCropSize));
  }

  private async cropSelectedFiles(files: File[]): Promise<File[]> {
    const croppedFiles: File[] = [];

    for (const file of files) {
      const cropped = await this.openCropModalForFile(file);
      if (cropped) {
        croppedFiles.push(cropped);
      }
    }

    return croppedFiles;
  }

  private openCropModalForFile(file: File): Promise<File | null> {
    return new Promise((resolve) => {
      const image = new Image();
      const objectUrl = URL.createObjectURL(file);

      image.onload = () => {
        this.cropResolve = resolve;
        this.cropSourceFile = file;
        this.cropSourceImage = image;
        this.cropSourceObjectUrl = objectUrl;
        this.cropModalImageSrc = objectUrl;
        this.cropModalFileName = file.name;
        this.cropImageWidth = image.naturalWidth;
        this.cropImageHeight = image.naturalHeight;

        const minDimension = Math.min(this.cropImageWidth, this.cropImageHeight);
        this.cropMaxZoom = this.clamp(minDimension / 64, 1, 20);
        this.cropZoom = 1;
        this.cropCenterX = this.cropImageWidth / 2;
        this.cropCenterY = this.cropImageHeight / 2;
        this.cropPreviewScale = Math.min(340 / this.cropImageWidth, 340 / this.cropImageHeight, 1);
        this.cropIsApplying = false;
        this.showCropModal = true;
        this.clampCropCenter();
        this.cdr.detectChanges();
      };

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        this.snackBar.open('Unable to open selected image for cropping.', 'Close', {
          duration: 4500,
          panelClass: ['error-snackbar'],
          verticalPosition: 'top',
          horizontalPosition: 'center',
        });
        resolve(null);
      };

      image.src = objectUrl;
    });
  }

  private resolveCropRequest(result: File | null): void {
    const resolver = this.cropResolve;
    this.cropResolve = null;

    if (resolver) {
      resolver(result);
    }

    this.resetCropModalState();
  }

  private resetCropModalState(): void {
    if (this.cropSourceObjectUrl) {
      URL.revokeObjectURL(this.cropSourceObjectUrl);
      this.cropSourceObjectUrl = null;
    }

    this.showCropModal = false;
    this.cropModalFileName = '';
    this.cropModalImageSrc = null;
    this.cropIsApplying = false;
    this.cropSourceFile = null;
    this.cropSourceImage = null;
    this.cropImageWidth = 0;
    this.cropImageHeight = 0;
    this.cropZoom = 1;
    this.cropMaxZoom = 1;
    this.cropCenterX = 0;
    this.cropCenterY = 0;
    this.cropPreviewScale = 1;
    this.cdr.detectChanges();
  }

  private clampCropCenter(): void {
    this.cropCenterX = this.clamp(this.cropCenterX, this.cropCenterXMin, this.cropCenterXMax);
    this.cropCenterY = this.clamp(this.cropCenterY, this.cropCenterYMin, this.cropCenterYMax);
  }

  private get currentCropSize(): number {
    const shortestSide = Math.min(this.cropImageWidth, this.cropImageHeight);
    if (shortestSide <= 0) {
      return 1;
    }

    return shortestSide / this.cropZoom;
  }

  private clamp(value: number, min: number, max: number): number {
    if (!Number.isFinite(value)) {
      return min;
    }

    if (max < min) {
      return min;
    }

    return Math.min(Math.max(value, min), max);
  }

  private async buildCroppedSquareFile(sourceFile: File, image: HTMLImageElement): Promise<File> {
    const cropSize = this.currentCropSize;
    const startX = this.cropCenterX - cropSize / 2;
    const startY = this.cropCenterY - cropSize / 2;
    const outputSize = Math.max(1, Math.round(cropSize));

    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas context unavailable');
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(
      image,
      startX,
      startY,
      cropSize,
      cropSize,
      0,
      0,
      outputSize,
      outputSize
    );

    const fileType = sourceFile.type && sourceFile.type.startsWith('image/')
      ? sourceFile.type
      : 'image/jpeg';

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, fileType, 0.95);
    });

    if (!blob) {
      throw new Error('Failed to generate cropped image blob');
    }

    const extension = this.extensionForMimeType(fileType);
    const croppedName = this.withCropSuffix(sourceFile.name, extension);
    return new File([blob], croppedName, { type: fileType, lastModified: Date.now() });
  }

  private extensionForMimeType(mimeType: string): string {
    const normalized = mimeType.toLowerCase();
    if (normalized.includes('png')) {
      return 'png';
    }
    if (normalized.includes('webp')) {
      return 'webp';
    }
    if (normalized.includes('gif')) {
      return 'gif';
    }

    return 'jpg';
  }

  private withCropSuffix(fileName: string, extension: string): string {
    const dotIndex = fileName.lastIndexOf('.');
    const baseName = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
    return `${baseName}-square.${extension}`;
  }

  private async uploadPendingImage(image: ImageTile, file: File): Promise<void> {
    try {
      image.preview = await this.toDataUrl(file);
      this.cdr.detectChanges();

      const presignedResponse = await firstValueFrom(
        this.s3FileService.createPresignedUploadUrl(file)
      );

      await lastValueFrom(
        this.s3FileService.uploadToS3(presignedResponse.upload_url, file)
      );

      image.key = presignedResponse.key;
      image.isUploading = false;
      image.error = undefined;
    } catch (_error) {
      image.key = null;
      image.isUploading = false;
      image.error = 'Upload failed';
      this.imageUploadError =
        'Some images failed to upload. Remove the failed images and try again.';
        this.onImageUploadError.emit(this.imageUploadError)
    } finally {
      this.onSingleImageUploadFinished.emit(image)
    }
  }

  private toDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve((reader.result as string) ?? '');
      reader.onerror = () => reject(new Error('Failed to read image file.'));
      reader.readAsDataURL(file);
    });
  }

  ngOnDestroy(): void {
    if (this.cropResolve) {
      this.cropResolve(null);
      this.cropResolve = null;
    }

    if (this.cropSourceObjectUrl) {
      URL.revokeObjectURL(this.cropSourceObjectUrl);
      this.cropSourceObjectUrl = null;
    }
  }

}
